import { useRequest } from 'ahooks';
import { FC, useEffect, useMemo, useState } from 'react';
import { getUserInfo } from './service';
import { Button, Modal, Select, Tooltip } from 'antd';
import defaultAvatarSvg from './defaultAvatar.svg';
import { NickName } from './components/NickName';
import { LanguageSetting } from './components/LanguageSetting';
import aboutSvg from './about.svg';
import contact from './contact.svg';
import logoutSvg from './logout.svg';
import { tokenKey, userInfoKey } from '../../../../utils/localStore';
import { DefaultOptionType } from 'antd/es/select';
import useI18n from '../../../../hooks/useI18n';
import { Languages, useLanguageStore } from '../../../../hooks';
import useUrlState from '@ahooksjs/use-url-state';

export interface IPersonCenterProps {
  isLogin: boolean;
  setIsLogin: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PersonCenter: FC<IPersonCenterProps> = (props) => {
  const { isLogin, setIsLogin } = props;

  const { data, run } = useRequest(getUserInfo, {
    ready: isLogin,
  });
  const userInfo = data?.data.data;

  const [urlState, setUrlState] = useUrlState({ id: undefined });

  const onOpenChange = (visible: boolean) => {
    if (visible) {
      run();
    }
  };

  const logout = () => {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(userInfoKey);
    localStorage.removeItem('lang');
    setUrlState({
      id: undefined,
    });
    window.location.reload();
    setIsLogin(false);
  };

  const i18n = useI18n();

  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [selectCert, setSelectCert] = useState('');
  const [certOptions, setCertOptions] = useState<DefaultOptionType[]>([]);

  useEffect(() => {
    if (data?.data.data) {
      setSelectCert(data.data.data.neutralCertList[0].certYears);
      setCertOptions(
        data.data.data.neutralCertList.map((item) => ({
          label: item.certYears,
          value: item.certUrl,
        })),
      );
    }
  }, [data?.data.data]);

  const certUrl = useMemo(() => {
    return certOptions.find((item) => item.label === selectCert)?.value;
  }, [selectCert, certOptions]);

  const info = data?.data.data;

  const { lang } = useLanguageStore();
  if (isLogin) {
    return (
      <Tooltip
        arrow={false}
        // open
        overlayStyle={{
          maxWidth: 'none',
        }}
        onOpenChange={onOpenChange}
        placement="bottomRight"
        color="#fff"
        title={
          <div className="w-[22rem] text-[#04252B]">
            <NickName name={data?.data.data.nickName} />
            <div className="w-[20rem] h-[7rem] bg-[#F6F9FC] mx-auto mt-2 mb-4 rounded-2xl overflow-hidden">
              <div className="w-full h-[4.75rem] flex items-center">
                <div className="w-1/2 flex flex-col items-center">
                  <div className="mb-1">
                    <span className="text-[#04252B] text-xs mr-1">
                      {i18n('Cumulative emissions')}
                    </span>
                    <span className="text-[#999EA4]">
                      {info?.totalOffsettingUnit}
                    </span>
                  </div>
                  <div className="text-[#04252B] text-sm font-medium">
                    {info?.totalReduction}
                  </div>
                </div>
                <div className="w-1/2 flex flex-col items-center">
                  <div className="mb-1">
                    <span className="text-[#04252B] text-xs mr-1">
                      {i18n('carbon credit offsets')}
                    </span>
                    <span className="text-[#999EA4]">
                      {info?.totalReductionUnit}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-[#08C0AC]">
                    {info?.totalOffsetting}
                  </div>
                </div>
              </div>
              <div className="w-full h-[2.25rem] bg-[#F0F5F9] px-4 py-[0.38rem] flex justify-between items-center">
                <div className="flex items-center">
                  <div className="text-xs text-[#0CBF9F] ml-1">
                    {i18n('We achieve carbon neutrality every year')}
                  </div>
                </div>
                <div
                  className="text-xs text-[#999EA4] cursor-pointer underline"
                  onClick={() => setCertModalOpen(true)}
                >
                  {i18n('Historical carbon neutrality certificates')}
                </div>
                <Modal
                  width={800}
                  footer={null}
                  open={certModalOpen}
                  title={i18n('Carbon Neutrality Certificate')}
                  onCancel={() => setCertModalOpen(false)}
                >
                  <div className="flex flex-col items-center">
                    <Select
                      placeholder="Borderless"
                      variant="borderless"
                      value={selectCert}
                      style={{ flex: 1 }}
                      options={certOptions}
                    />
                    {certUrl && (
                      <img
                        className="w-[36rem] mt-4"
                        src={certUrl.toString()}
                        alt=""
                      />
                    )}
                    <div className="w-full flex justify-end mt-4">
                      <Button onClick={() => setCertModalOpen(false)}>
                        {i18n('close')}
                      </Button>
                    </div>
                  </div>
                </Modal>
              </div>
            </div>
            <LanguageSetting />
            <div className="w-[19rem] py-2 mx-auto border-t border-b border-solid border-[#EBEDF0]">
              <div
                className="py-3 text-sm flex cursor-pointer"
                onClick={() => setAboutModalOpen(true)}
              >
                <img src={aboutSvg} className="mr-2" alt="" />
                {i18n('aboutCarbonAI')}
              </div>
              <Modal
                width="48rem"
                footer={null}
                title={i18n('aboutCarbonAI')}
                open={aboutModalOpen}
                onCancel={() => setAboutModalOpen(false)}
              >
                <div className="text-sm text-[#0CBF9F] mb-3 mt-4">
                  {i18n('Introduction to CarbonAI')}
                </div>
                <div className="flex flex-col gap-4 text-sm">
                  <div>{i18n('aboutMain1')}</div>
                  <div>{i18n('aboutMain2')}</div>
                  <div>{i18n('aboutMain3')}</div>
                </div>
                <div className="text-sm text-[#0CBF9F] mb-3 mt-4">
                  {i18n('aboutCapacityTitle')}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-[#F6F9FC] rounded-xl grow-0">
                    <div className="text-sm text-[#04252B] mb-1">
                      {i18n('aboutCapacityList1Title')}
                    </div>
                    <div className="text-xs text-[#04252BA3]">
                      {i18n('aboutCapacityList1Content')}
                    </div>
                  </div>
                  <div className="p-4 bg-[#F6F9FC] rounded-xl grow-0">
                    <div className="text-sm text-[#04252B] mb-1">
                      {i18n('aboutCapacityList2Title')}
                    </div>
                    <div className="text-xs text-[#04252BA3]">
                      {i18n('aboutCapacityList2Content')}
                    </div>
                  </div>
                  <div className="p-4 bg-[#F6F9FC] rounded-xl grow-0">
                    <div className="text-sm text-[#04252B] mb-1">
                      {i18n('aboutCapacityList3Title')}
                    </div>
                    <div className="text-xs text-[#04252BA3]">
                      {i18n('aboutCapacityList3Content')}
                    </div>
                  </div>
                </div>
                <div className="w-full flex justify-end mt-6">
                  <Button onClick={() => setAboutModalOpen(false)}>
                    {i18n('close')}
                  </Button>
                </div>
              </Modal>
              <div
                className="py-3 text-sm flex cursor-pointer"
                onClick={() => {
                  window.open(
                    `https://www.carbonstop.com/${lang === Languages.zh ? 'zh' : 'en'}`,
                    '_blank',
                  );
                }}
              >
                <img src={contact} className="mr-2" alt="" />{' '}
                {i18n('contactUs')}
              </div>
            </div>
            <div
              className="w-[19rem] mx-auto py-3 text-sm flex cursor-pointer"
              onClick={logout}
            >
              <img src={logoutSvg} className="mr-2" alt="" /> {i18n('signOut')}
            </div>
          </div>
        }
      >
        <div className="w-8 h-8 cursor-pointer">
          <img
            className="w-8 h-8"
            src={userInfo?.avatarUrl || defaultAvatarSvg}
            alt=""
          />
        </div>
      </Tooltip>
    );
  } else {
    return <Button>{i18n('login')}</Button>;
  }
};
